/**
 * OrderSSEContext
 * Provides SSE subscription for order updates that persists across navigation
 * between Orders and OrderDetails pages
 */

import React, { createContext, useContext, useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import * as Sentry from '@sentry/react';
import { useSSESubscription } from '../hooks/useSSESubscription';
import { SSEStreamType, OrderUpdateData, SSEConnectionState } from '../services/sse/types';
import { addNotification } from '../store/notifications.slice';
import { AllOrderManagementState, updateNewOrdersCount } from '../store/allOrders.slice';
import { getSession } from '../utils/session';
import { isShopAdmin, isShopSupervisor, isShopPicker } from '../utils/roles';
import { shouldSuppressNotification } from '../utils/notificationFilters';

// Debug logging
const LOG_PREFIX = '[OrderSSE]';
const log = (...args: unknown[]) => console.log(LOG_PREFIX, ...args);
const logError = (...args: unknown[]) => console.error(LOG_PREFIX, ...args);

interface OrderSSEContextType {
  connectionState: SSEConnectionState;
}

const OrderSSEContext = createContext<OrderSSEContextType | null>(null);

export const useOrderSSE = () => {
  const context = useContext(OrderSSEContext);
  if (!context) {
    throw new Error('useOrderSSE must be used within OrderSSEProvider');
  }
  return context;
};

interface OrderSSEProviderProps {
  children: React.ReactNode;
}

export const OrderSSEProvider: React.FC<OrderSSEProviderProps> = ({ children }) => {
  const location = useLocation();
  const dispatch = useDispatch();

  const shopId = useSelector(
    (state: { allOrderManagement: AllOrderManagementState }) =>
      state?.allOrderManagement?.shopId
  );

  const role = getSession('role');
  const canManageShopOrders =
    isShopAdmin(role) || isShopSupervisor(role) || isShopPicker(role);

  // Track active connection to ensure only one connection per shop
  const activeConnectionRef = useRef<string | null>(null);

  // Check if we're on orders or order details page
  // Orders page: /orders or /orders?...
  // Order details page: /orders/:externalOrderId
  const isOrdersPage = location.pathname === '/orders' || location.pathname.startsWith('/orders?');
  const isOrderDetailsPage = /^\/orders\/[^/]+$/.test(location.pathname);
  const isOrdersEcosystem = isOrdersPage || isOrderDetailsPage;

  // Handler for SSE order update events
  const handleOrderUpdate = useCallback((data: OrderUpdateData) => {
    log('Order update received via SSE:', data);

    try {
      // Validate required fields in OrderUpdateData
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid order update data: data is not an object');
      }

      if (!data.externalOrderId) {
        throw new Error('Invalid order update data: missing externalOrderId');
      }

      if (!data.subType || !['incoming', 'modification', 'statusUpdate'].includes(data.subType)) {
        throw new Error(`Invalid order update data: invalid subType "${data.subType}"`);
      }

      if (!data.orderId) {
        throw new Error('Invalid order update data: missing orderId');
      }

      if (!data.title) {
        throw new Error('Invalid order update data: missing title or body');
      }

      log('Validation passed - SubType:', data.subType, 'OrderId:', data.orderId);

      // Check if this is an offline order that should suppress notifications
      const shouldSuppress = shouldSuppressNotification(data);
      if (shouldSuppress) {
        log('Suppressing offline order notification - orderType:', data.orderType);
      }

      Sentry.addBreadcrumb({
        category: 'sse',
        message: `Order update received: ${data.subType}`,
        level: 'info',
        data: {
          subType: data.subType,
          orderId: data.orderId,
          externalOrderId: data.externalOrderId,
          newOrdersCount: data.newOrdersCount,
          suppressed: shouldSuppress,
        },
      });

      // Update new orders count in Redux for badge display if provided
      if (typeof data.newOrdersCount === 'number') {
        log('Updating new orders count to:', data.newOrdersCount);
        dispatch(updateNewOrdersCount(data.newOrdersCount));
      }

      // Only dispatch notification to Redux if not suppressed
      // This controls the visual notification and badge increment
      if (!shouldSuppress) {
        log('Dispatching notification - Title:', data.title, 'Body:', data.body);
        
        // Build notification data object with only defined fields
        const notificationData: Record<string, any> = {
          type: data.type,
          subType: data.subType,
          orderId: data.orderId,
          externalOrderId: data.externalOrderId,
        };
        
        // Only include optional fields if they exist
        if (data.customerName) {
          notificationData.customerName = data.customerName;
        }
        if (data.customerPhone) {
          notificationData.customerPhone = data.customerPhone;
        }
        if (data.orderTotal) {
          notificationData.orderTotal = data.orderTotal;
        }
        
        dispatch(addNotification({
          title: data.title,
          body: data.body,
          data: notificationData,
        }));
        log('Notification dispatched to Redux store');
      } else {
        log('Skipping notification dispatch for suppressed offline order');
      }

      // Always dispatch custom event for orders list update
      // This ensures offline orders are still added to the orders list
      const customEvent = new CustomEvent('orderSSEUpdate', { detail: data });
      window.dispatchEvent(customEvent);
      log('Custom event orderSSEUpdate dispatched');
    } catch (error) {
      logError('Error processing order update:', error);

      // Log error to Sentry with event data
      Sentry.captureException(error, {
        tags: {
          component: 'OrderSSEContext',
          errorType: 'malformed_data',
        },
        extra: {
          eventData: data,
          errorMessage: error instanceof Error ? error.message : 'Unknown error',
        },
      });

      // Continue processing - don't break the application
      // The error is logged but we don't throw or show UI errors
    }
  }, [dispatch]); // Stable dependency - dispatch doesn't change

  // Subscribe to SSE order updates only when in orders ecosystem
  // The enabled flag controls connection lifecycle:
  // - Connection opens when: canManageShopOrders && shopId && isOrdersEcosystem
  // - Connection closes when: navigating away (isOrdersEcosystem becomes false)
  // - Connection persists when: navigating between orders and order details
  // 
  // Connection Uniqueness (Requirement 12.1):
  // - Only one SSE connection per shop is allowed at a time
  // - activeConnectionRef tracks the current shop's connection
  // - If shopId changes, the old connection is automatically closed by useSSESubscription cleanup
  // - New connection is only created if shopId is different from active connection
  // 
  // Graceful Degradation:
  // - If SSE connection fails, the system automatically falls back to push notifications
  // - Push notifications continue to work independently of SSE
  // - No error messages are shown to users - all errors are logged to Sentry
  // - UI remains fully functional even when SSE is unavailable

  // Check if we need to create a new connection or if one already exists for this shop
  const shouldCreateConnection = canManageShopOrders && !!shopId && isOrdersEcosystem;
  const isNewShop = shopId !== activeConnectionRef.current;

  // Update active connection ref when shopId changes
  useEffect(() => {
    if (shouldCreateConnection && isNewShop) {
      activeConnectionRef.current = shopId;
      Sentry.addBreadcrumb({
        category: 'sse',
        message: `Setting active connection for shop: ${shopId}`,
        level: 'info',
      });
    } else if (!shouldCreateConnection && activeConnectionRef.current) {
      Sentry.addBreadcrumb({
        category: 'sse',
        message: `Clearing active connection for shop: ${activeConnectionRef.current}`,
        level: 'info',
      });
      activeConnectionRef.current = null;
    }
  }, [shouldCreateConnection, isNewShop, shopId]);

  const { connectionState } = useSSESubscription<OrderUpdateData>(
    SSEStreamType.ORDER_UPDATES,
    shopId,
    handleOrderUpdate,
    shouldCreateConnection
  );

  // Log connection state changes to Sentry for debugging
  useEffect(() => {
    if (isOrdersEcosystem) {
      Sentry.addBreadcrumb({
        category: 'sse',
        message: `Order SSE connection state changed: ${connectionState}`,
        level: connectionState === 'error' ? 'warning' : 'info',
        data: {
          shopId,
          canManageShopOrders,
          pathname: location.pathname,
          isOrdersEcosystem,
        },
      });
    }
  }, [connectionState, shopId, canManageShopOrders, isOrdersEcosystem, location.pathname]);

  // Log when connection should close due to navigation
  useEffect(() => {
    if (!isOrdersEcosystem) {
      Sentry.addBreadcrumb({
        category: 'sse',
        message: 'Not in orders ecosystem - SSE connection disabled',
        level: 'info',
        data: {
          pathname: location.pathname,
        },
      });
    }
  }, [isOrdersEcosystem, location.pathname]);

  // Cleanup effect to ensure activeConnectionRef is cleared on unmount
  // This prevents memory leaks by ensuring refs are properly cleaned up
  useEffect(() => {
    return () => {
      if (activeConnectionRef.current) {
        Sentry.addBreadcrumb({
          category: 'sse',
          message: `Cleanup: Clearing active connection ref for shop: ${activeConnectionRef.current}`,
          level: 'info',
        });
        activeConnectionRef.current = null;
      }
    };
  }, []);

  return (
    <OrderSSEContext.Provider value={{ connectionState }}>
      {children}
    </OrderSSEContext.Provider>
  );
};
